/**
 * DECISIONS 216 (4) (the owner: "you see those battle maps from Ukraine war even, and it's these projections and incursions
 * into regions, and so you have these squiggly lines that define roughly who holds what territory"; on its win, 219: "don't
 * know we'll try stuff"), DIRECTION §0.12: territory and fronts. Each vertex of the planet's route graph (core/planetRoutes.ts)
 * belongs to the side nearest it over the routes on foot from what that side holds — its base and the objectives it owns —
 * so the line between them follows the ground (a ridge holds it, a valley carries it on); within TERRITORY.contestPeople of
 * equal it is contested. Derived from the state (who holds what), never stored: pure, memoised on that. The planet table
 * (orbital-design-3) draws it in place of its held-points field, as the side knows it (`asKnownBy`: the fairness rule);
 * `ModeConfig.winConditions`' 'territory' reads its shares from the truth.
 */
import type { GameState, Vec3, CapturePoint } from '../types';
import { TERRITORY, PERSON_BODY_HEIGHT } from '../constants';
import { planetRouteGraph, edgeCost, type PlanetRouteGraph, type Roads } from './planetRoutes';
import { roadsOf } from './roads';
import { nearestSurfaceVertex } from './surface';
import { battlePlanetIndex } from './actionSetup';
import { personSetOf } from './scaleProfile';
import { sitesAsKnown } from './objectiveIntel';

/** A planet's territory: each route vertex's side (−1 contested or no one's), and each side's share of the vertices. */
export interface TerritoryField { planetIndex: number; owner: Int8Array; share: Record<number, number>; contested: number }

const memo = new WeakMap<PlanetRouteGraph, Map<string, TerritoryField>>();

/** What each side holds on the battle planet: its base and its owned objectives (the seeds of its territory), in a fixed order. */
function holdings(state: GameState, planetIndex: number, sites: readonly CapturePoint[]): Map<number, Vec3[]> {
  const out = new Map<number, Vec3[]>();
  const add = (team: number, d: Vec3) => { const l = out.get(team) ?? []; l.push(d); out.set(team, l); };
  const teams = [...new Set(state.players.map(p => p.teamIndex))].sort((a, b) => a - b);
  for (const t of teams) {
    const base = state.board?.bases.find(b => b.team === t && b.planetIndex === planetIndex)?.dir ?? state.actionHomes?.[t];
    if (base) add(t, base);
  }
  for (const s of [...sites].sort((a, b) => a.id - b.id)) if (s.owner !== null && s.planetIndex === planetIndex) add(s.owner, s.surfaceDir);
  return out;
}

/**
 * The battle planet's territory now; null without a battle planet or with fewer than two sides holding anything. From the
 * truth (who holds each objective: the sim's territory win), or `asKnownBy` a side at a tick (the planet table's): its own
 * base and objectives exactly, the enemy's base (the board is known), and the enemy's objectives as that side knows them
 * (core/objectiveIntel.ts `sitesAsKnown`: with objectives known roughly, one it hasn't found is nobody's as far as it knows,
 * so it seeds nothing — not even at its circle's centre, which would draw the enemy's ground round a guess; one found, its
 * holder as last seen, going stale). The memo is keyed by the seeds, so a side's view is cached on its known holdings and
 * the same holdings give the same field whoever's they are.
 */
export function territoryField(state: GameState, asKnownBy?: { team: number; tick: number }): TerritoryField | null {
  const bi = battlePlanetIndex(state);
  if (bi < 0) return null;
  const planet = state.planets[bi];
  const truth = state.modeState.points?.sites ?? [];
  const held = holdings(state, bi, asKnownBy ? sitesAsKnown(state, asKnownBy.team, truth, asKnownBy.tick) : truth);
  if (held.size < 2) return null;
  const g = planetRouteGraph(planet);
  const seeds = [...held].map(([t, dirs]) => [t, [...new Set(dirs.map(d => g.nodeOf[nearestSurfaceVertex(planet, d)]))].sort((a, b) => a - b)] as const);
  const roads = roadsOf(state, bi);   // (with roads on, territory carries farther along them)
  const key = `${bi}|${roads?.key ?? ''}|` + seeds.map(([t, v]) => `${t}:${v.join(',')}`).join('|');
  let byKey = memo.get(g);
  if (!byKey) { byKey = new Map(); memo.set(g, byKey); }
  const hit = byKey.get(key);
  if (hit) return hit;
  // each side's cost out from its holdings, on foot (a multi-source search over the route graph)
  const reach = seeds.map(([team, from]) => ({ team, cost: spread(g, from, planet.radius, roads) }));
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, band = TERRITORY.contestPeople * person;
  const owner = new Int8Array(g.count).fill(-1);
  const count: Record<number, number> = {};
  let contested = 0;
  for (let v = 0; v < g.count; v++) {
    let best = -1, bestC = Infinity, second = Infinity;
    for (const r of reach) {
      const c = r.cost[v];
      if (c < bestC) { second = bestC; bestC = c; best = r.team; } else if (c < second) second = c;
    }
    if (best < 0 || !Number.isFinite(bestC) || second - bestC < band) { contested++; continue; }
    owner[v] = best;
    count[best] = (count[best] ?? 0) + 1;
  }
  const share: Record<number, number> = {};
  for (const [t] of seeds) share[t] = (count[t] ?? 0) / g.count;
  const field: TerritoryField = { planetIndex: bi, owner, share, contested: contested / g.count };
  byKey.set(key, field);
  while (byKey.size > TERRITORY.memo) byKey.delete(byKey.keys().next().value!);
  return field;
}

/** A side's cost to every vertex, out from `from` on foot (Dijkstra; ties to the lower vertex). */
function spread(g: PlanetRouteGraph, from: readonly number[], radius: number, roads: Roads | null): Float64Array {
  const cost = new Float64Array(g.count).fill(Infinity), done = new Uint8Array(g.count);
  const hv: number[] = [], hc: number[] = [];
  const less = (i: number, j: number) => hc[i] < hc[j] || (hc[i] === hc[j] && hv[i] < hv[j]);
  const swap = (i: number, j: number) => { const v = hv[i], c = hc[i]; hv[i] = hv[j]; hc[i] = hc[j]; hv[j] = v; hc[j] = c; };
  const push = (v: number, c: number) => { hv.push(v); hc.push(c); for (let i = hv.length - 1; i > 0;) { const p = (i - 1) >> 1; if (!less(i, p)) break; swap(i, p); i = p; } };
  const pop = (): number => {
    const v = hv[0], lv = hv.pop()!, lc = hc.pop()!;
    if (hv.length) { hv[0] = lv; hc[0] = lc; for (let i = 0; ;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < hv.length && less(l, m)) m = l; if (r < hv.length && less(r, m)) m = r; if (m === i) break; swap(i, m); i = m; } }
    return v;
  };
  for (const v of from) { cost[v] = 0; push(v, 0); }
  while (hv.length) {
    const u = pop();
    if (done[u]) continue;
    done[u] = 1;
    for (let k = g.start[u]; k < g.start[u + 1]; k++) {
      const w = g.nbr[k];
      if (done[w]) continue;
      const c = cost[u] + edgeCost(g, u, w, 'foot', radius, roads);   // (out from the holdings: the walk from u to w)
      if (c < cost[w]) { cost[w] = c; push(w, c); }
    }
  }
  return cost;
}

/** The side whose territory `dir` lies in (−1: contested or no one's), from a field. */
export function territoryAt(state: GameState, field: TerritoryField, dir: Vec3): number {
  const planet = state.planets[field.planetIndex];
  return field.owner[planetRouteGraph(planet).nodeOf[nearestSurfaceVertex(planet, dir)]];
}
